import React, {useCallback, useEffect, useMemo, useState} from "react";
import {IStateFromProps, IDispatchFromProps} from "shared-components/booking-options-section/MenuOption/types";
import {IProps} from './types';
import ReactUtilsService from "app/services/reactUtils.service";
import AppThemeService from "app/services/theme/appTheme.service";
import {ISimpleBookingOption, servicePaymentType} from "app/types/client.types";
import {IMenuOptionsExtrasItem} from "shared-components/booking-options-section/MenuOptionsExtras/types";
import MenuOptionReducerService from "app/fakeReducer/menuOption.reducer";
import {
  loadStatus,
  IBookingMenuOption,
  IBookingMenuOptionExtras,
  PricingTypes,
  IScheduleService,
  IServiceItem
} from "shared-types/index";
import MenuOptionActionService from "app/actions/menuOption.actions";
import {MenuOptionsState} from "external-components/booking-options-section/ChildMenuOptionOverlay/types";
import store from "appRoot/Store";
import MenuOptionLegacy from "shared-components/booking-options-section/MenuOption/index";
import MenuOption from "external-components/booking-options-section/MenuOption/index";
import {useSelector} from "react-redux";
import {BookingCreatorState} from "booking-creator/rootReducer";
import {DiaryRootState} from "appRoot/rootReducer";
import {cloneDeep, isEqual} from "lodash-es";

const NS = 'MenuOptionContainer';

/**
 * This container component cannot be declared within AngularJS HTML partials.
 * It can only be declared as a React (JSX) component and all AngularJS dependencies
 * must be injected into MenuOptionReducerService (see setup.controller.js).
 */

export default function MenuOptionContainer(props: IProps) {

  let bookingTime: Date;

  const [selectedMenuOptions, setSelectedMenuOptions] = useState<IBookingMenuOption[]>([]);
  const [legacySelectedService, setLegacySelectedService] = useState<IScheduleService | IServiceItem | null>(null);


  //prettier-ignore
  const immutableSelectedMenuOptions = useSelector((state: BookingCreatorState) => state.displayReducer.activeBooking?.selectedMenuOptions);
  //prettier-ignore
  let selectedService = useSelector((state: BookingCreatorState) => state.displayReducer.selectedService);
  //prettier-ignore
  let cachedMenuOptionDetails = useSelector((state: BookingCreatorState) => state.bookingOptionsUpsellReducer.cachedMenuOptionDetails);
  //prettier-ignore
  let editCachedMenuOptionDetails = useSelector((state: BookingCreatorState) => state.bookingOptionsUpsellReducer.editCachedMenuOptionDetails);
  let currency = useSelector((state: DiaryRootState) => state.venueReducer.currency);


  useEffect(() => {
    if (props.stateType === MenuOptionsState.DiaryLegacyState) {

      const legacyState = MenuOptionReducerService.getState();
      currency = legacyState.currency;
      bookingTime = legacyState.booking.time;
      cachedMenuOptionDetails = legacyState.cachedMenuOptionDetails;
      editCachedMenuOptionDetails = legacyState.editCachedMenuOptionDetails;
      setSelectedMenuOptions(legacyState.booking.selectedMenuOptions);
    }
  }, []);

  useEffect(() => {
    if (props.stateType === MenuOptionsState.DiaryLegacyState) {
      return;
    }
    // This is for Booking Creator
    if (!isEqual(immutableSelectedMenuOptions, selectedMenuOptions)) {
      setSelectedMenuOptions(cloneDeep(immutableSelectedMenuOptions));
    }
  }, [immutableSelectedMenuOptions]);



  useEffect(() => {
    if (props.stateType === MenuOptionsState.DiaryLegacyState) {
      // old legacy way of getting selected service
      MenuOptionReducerService.getSelectedService()
        .then(service => {
          setLegacySelectedService(service);
        });
    }
  }, [bookingTime]);

  const getExtrasDetails = useCallback(({menuOptionId, quantity}: IBookingMenuOption): IMenuOptionsExtrasItem => {

    const cachedDetails = cachedMenuOptionDetails?.length ? cachedMenuOptionDetails : editCachedMenuOptionDetails;

    const cachedOpt = cachedDetails.find(val => menuOptionId === val.id);
    if (cachedOpt) {
      const {id, label, price, paymentType} = cachedOpt as ISimpleBookingOption;
      return {id, label, price, paymentType, quantity, status: loadStatus.loaded};
    }

    // these will get loaded from backend and added to cachedMenuOptionDetails
    return {
      id: menuOptionId,
      label: null,
      price: null,
      paymentType: servicePaymentType.noPayment,
      quantity,
      status: loadStatus.idle
    };
  }, [cachedMenuOptionDetails?.length, editCachedMenuOptionDetails?.length]);// check length so not rerendered when empty

  const extraDetails: IMenuOptionsExtrasItem[] = [];
  if (selectedMenuOptions) {
    selectedMenuOptions.forEach(({menuOptionId, extras}: { menuOptionId: string, extras?: IBookingMenuOptionExtras }) => {
      if (props.id === menuOptionId && extras) {
        const {explicitChildMenuOptions, implicitChildMenuOptions} = extras;

        explicitChildMenuOptions.forEach(o1 => {
          o1.forEach(o2 => {
            extraDetails.push(getExtrasDetails(o2));
          });
        });

        implicitChildMenuOptions.forEach(o => {
          extraDetails.push(getExtrasDetails(o));
        });
      }
    });
  }

  const hasExtras = !!(props.allChildMenuOptionIds && props.allChildMenuOptionIds.length);

  const extrasHasPayment = useMemo(() => {
    if (!hasExtras) {
      return false;
    }

    const cachedDetails = editCachedMenuOptionDetails;
    // gets details based on child ids, then if any child options have a payment, sets extrasHasPayment to true
    const details = props.allChildMenuOptionIds.reduce((acc, id) => {
      const det = cachedDetails && cachedDetails.find((o: { id: string; }) => o.id === id);
      if (det) {
        acc.push(det);
      }
      return acc;
    }, []);

    return details.some(o => o.paymentType !== servicePaymentType.noPayment)
  }, [hasExtras, props.allChildMenuOptionIds, editCachedMenuOptionDetails.length]);

  const stateFromProps: IStateFromProps = {
    currency,
    theme: AppThemeService.getAppThemeAsWidgetTheme(),
    isStacked: true,
    extraDetails,
    extrasHasPayment,
  };

  const effectiveSelectedService = props.stateType === MenuOptionsState.DiaryLegacyState
    ? legacySelectedService
    : selectedService;

  const updatedProps : IProps = {
    ...props,
    pricingType: effectiveSelectedService?.paymentDetails?.pricingType || null,
    updateCachedMenuOptionDetails: null,
    description: null, // Removing description for BO in diary
    isUpsell: false // forces to use narrow version
  }

  const handlers: IDispatchFromProps = {

    handleIdleCachedMenuOptionDetails: (extraDetails: IMenuOptionsExtrasItem[]) => {
      const ids: string[] = [];
      extraDetails.forEach(o => {
        if (o.status === loadStatus.idle) {
          ids.push(o.id);
        }
      });

      if (ids.length) { // @todo: figure out if this actually ever gets called. severity: low
        MenuOptionActionService.updateCachedMenuOptionDetails(ids);
      }
    },
  }

  return ReactUtilsService.containerWrap(
    props.stateType === MenuOptionsState.DiaryLegacyState
      ? <MenuOptionLegacy {...updatedProps} {...stateFromProps} {...handlers} />
      : <MenuOption {...updatedProps} {...stateFromProps} {...handlers} />
  );
}
